import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types'
import Moment from 'moment';
import DatePicker from '../../Common/DatePicker/DatePicker';
import Drawer from '../../Common/Drawer/Drawer';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Icon from '../../Common/Icon';

import * as types from '../../../constants/actionTypes';
import { DATE_FORMAT } from '../../../constants/constants';

import CalculateDatesDrawer from './CalculateDatesDrawer';
import LocationTaskDetailDrawerForm from './LocationTaskDetailDrawerForm';

/**
 * LOCATION-CREATED Task Detail: Add/Edit a task at the location.  These
 * tasks will not be associated with a baseline task at all.  They're created
 * directly at one particular location.
 */
export class LocationTaskDetailDrawer extends Component {

    constructor(props, context) {
        super(props, context);

        this.state = {
            isSaving: false,
            isFormValid: true,
            isEditStartDate: false,
            isEditTargetDate: false,
            showCalculateDatesDrawer: false,
            completeStatusId: 0,
        }

        this.dropzoneRef = React.createRef();
    }

    componentDidMount = () => {
        const isAddMode = this.isAddMode();
        if (isAddMode || !this.props.task.startDate) {
            this.setState({ isEditStartDate: true, isEditTargetDate: isAddMode });
        }
    }

    isAddMode = () => {
        return this.props.task.id === 0;
    }

    isEditMode = () => {
        return this.props.task.id > 0;
    }

    onTaskChange = (name, value) => {
        let task = { ...this.props.task };
        task[name] = value;
        this.props.onTaskChange(task);
    }

    showContingentDrawer = () => {
        this.props.showContingentDrawer(this.props.task.contingentId);
    }

    onDeleteContingentClick = (e) => {
        e.preventDefault();
        let task = { ...this.props.task };
        task.contingentId = "";
        task.contingentTaskName = "";
        this.props.onTaskChange(task);
    }

    onSave = () => {
        if (this.validateForm()) {
            this.setState({ isFormValid: true });
            this.updateLocationTask();
        } else {
            this.setState({ isFormValid: false });
        }
    }

    validateForm = () => {
        const task = this.props.task;
        const validName = task.name && task.name.trim().length > 0;
        const validResponsible = task.responsibleUserId > 0;
        const validTargetDate = task.targetDate && task.targetDate.trim().length > 0;
        const validDept = (typeof task.departmentId === 'number' && task.departmentId > 0) ||
            (typeof task.departmentId === 'string' && task.departmentId.trim().length > 0);
        const validCommunity = (typeof task.communityId === 'number' && task.communityId > 0) ||
            (typeof task.communityId === 'string' && task.communityId.trim().length > 0);

        const validProjectIdValue = (typeof task.projectId === 'number' && task.projectId > 0) ||
            (typeof task.projectId === 'string' && task.projectId.trim().length > 0);
        const validRequiredProject = (!this.props.filters.isProjectWidgetActive || !this.props.filters.isProjectIdRequiredForLocationTask) || (this.props.filters.isProjectWidgetActive && this.props.filters.isProjectIdRequiredForLocationTask && validProjectIdValue);

        if (this.isAddMode()) {
            return validName && validResponsible && validTargetDate && validDept && validCommunity && validRequiredProject;
        } else {
            // Hiding Responsible User on Edit mode, so it's not a required field
            return validName && validTargetDate && validDept && validCommunity && validRequiredProject;
        }
    }

    updateLocationTask = async () => {
        try {
            this.setState({ isSaving: true });

            if (this.isAddMode()) {
                const links = this.props.task.links.filter(l => l.name.trim().length > 0);
                const request = {
                    ...this.props.task,
                    links: links
                }
                const response = await this.props.locationTaskActions.createLocationTask(request);
                if (response.type === types.CREATE_LOCATION_TASK_SUCCESS) {
                    Notification("success", "Success", "Task created successfully");
                    this.props.onComplete(response.payload.locationTaskId);

                } else if (response.type === types.CREATE_LOCATION_TASK_FAILURE) {
                    Notification("error", "Error", response.payload.message);
                    this.setState({ isSaving: false });
                }
            } else {
                const request = {
                    ...this.props.task,
                    locationTaskId: this.props.task.id,
                    targetDate: new Date(this.props.task.targetDate)
                }
                const response = await this.props.locationTaskActions.saveLocationTaskEditMode(request);
                if (response.type === types.UPDATE_LOCATION_TASK_EDIT_MODE_SUCCESS) {
                    Notification("success", "Success", "Task updated successfully");
                    this.props.onComplete();
                } else if (response.type === types.UPDATE_LOCATION_TASK_EDIT_MODE_FAILURE) {
                    Notification("error", "Error", response.payload.message);
                    this.setState({ isSaving: false });
                }
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isSaving: false });
        }
    }

    editStartDate = () => {
        this.setState({ isEditStartDate: true });
    }

    setStartDate = (value) => {
        if (this.areDatesInOrder(value, this.props.task.targetDate)) {
            this.onTaskChange("startDate", value);
            this.setState({ isEditStartDate: false });
        } else {
            /**
             * HACK: Have to toggle the visibility of the date picker to get it
             * to clear the displayed value, when it's initial value triggers
             * a validation error.  Otherwise, when its prop value doesn't 
             * change ("" => ""), it displays the selected date value even 
             * though the prop value is "".
             */
            this.onTaskChange("startDate", "");
            this.setState({
                isEditStartDate: false,
                isFormValid: false
            }, () => this.setState({
                isEditStartDate: true
            }));
            Notification("error", "Error", "Start Date cannot be after Target Date");
        }
    }

    editTargetDate = () => {
        this.setState({ isEditTargetDate: true });
    }

    setTargetDate = (value) => {
        if (this.areDatesInOrder(this.props.task.startDate, value)) {
            this.onTaskChange("targetDate", value);
            this.setState({ isEditTargetDate: false });
        } else {
            /**
             * HACK: Have to toggle the visibility of the date picker to get it
             * to clear the displayed value, when it's initial value triggers
             * a validation error.  Otherwise, when its prop value doesn't 
             * change ("" => ""), it displays the selected date value even 
             * though the prop value is "".
             */
            this.onTaskChange("targetDate", "");
            this.setState({
                isEditTargetDate: false,
                isFormValid: false
            }, () => this.setState({
                isEditTargetDate: true
            }));
            Notification("error", "Error", "Start Date cannot be after Target Date");
        }
    }

    /**
     * startDate and targetDate come in as D MMM YYYY
     */
    areDatesInOrder = (startDate, targetDate) => {
        if (!this.props.filters.isStartDateActive) {
            return true; // Do not error out if start date feature is disabled
        }

        if (!startDate || startDate.trim().length <= 0) {
            return true; // Do not error out if no start date value
        }

        if (!targetDate || targetDate.trim().length <= 0) {
            return true; // Do not error out if no target date value
        }

        if (targetDate === this.props.task.targetDate && startDate === this.props.task.startDate) {
            return true; // Do not error out if neither date has changed
        }

        const startMoment = Moment.utc(startDate, DATE_FORMAT);
        const targetMoment = Moment.utc(targetDate, DATE_FORMAT);
        if (startMoment.isAfter(targetMoment)) {
            return false;
        }
        return true;
    }

    /**
     * Calculate Dates Drawer
     */
    showCalculateDatesDrawer = (e) => {
        e.preventDefault();
        this.setState({ showCalculateDatesDrawer: true });
    }

    onCalculateDatesCancel = () => {
        this.setState({ showCalculateDatesDrawer: false });
    }

    onCalculateDatesComplete = (startDaysOut, startDate, targetDaysOut, targetDate) => {
        const task = {
            ...this.props.task,
            startDaysOut: startDaysOut,
            startDate: startDate,
            targetDaysOut: targetDaysOut,
            targetDate: targetDate
        };
        this.props.onTaskChange(task);
        this.setState({ showCalculateDatesDrawer: false });
    }

    renderStaticDateHeader = () => {
        const showStartDateValue = this.props.filters.isStartDateActive && this.props.task.startDate && !this.state.isEditStartDate;
        const showStartDateForm = this.props.filters.isStartDateActive && this.state.isEditStartDate;
        const showTargetDateValue = this.props.task.targetDate && !this.state.isEditTargetDate;
        const showTargetDateForm = this.state.isEditTargetDate;
        const showCalculateLink = !this.props.filters.isProjectWidgetActive && (showTargetDateValue || showTargetDateForm);
        const datePickerClass = (this.areDatesInOrder(this.props.task.startDate, this.props.task.targetDate)) ? "" : "invalid";

        return (
            <Fragment>
                {
                    showStartDateValue &&
                    <div className="col-4">
                        <div className="row">
                            <div className="readonly-date-view start-date col-12" onClick={this.editStartDate}>
                                <Icon name="calendar-alt" /><span className="ms-1">{this.props.task.startDate}</span>
                            </div>
                        </div>
                    </div>
                }
                {
                    showStartDateForm &&
                    <div className="col-4">
                        <DatePicker
                            showIcon
                            value={this.props.task.startDate ?? ""}
                            className={datePickerClass}
                            name="startStaticDate"
                            label=""
                            isRequired={true}
                            onChange={this.setStartDate} />
                    </div>
                }
                {
                    this.props.filters.isStartDateActive &&
                    <div className="col-1">
                        <div className="dash"><Icon name="minus" /></div>
                    </div>
                }
                {
                    showTargetDateValue &&
                    <div className="col-4">
                        <div className="row">
                            <div className="readonly-date-view target-date col" onClick={this.editTargetDate}>
                                <Icon name="calendar-alt" /><span className="ms-1">{this.props.task.targetDate}</span>
                            </div>
                        </div>
                    </div>
                }
                {
                    showTargetDateForm &&
                    <div className="col-4">
                        <div className="row">
                            <div className="col">
                                <DatePicker
                                    showIcon
                                    value={this.props.task.targetDate}
                                    className={datePickerClass}
                                    name="targetDate"
                                    label=""
                                    isRequired={true}
                                    onChange={this.setTargetDate} />
                            </div>
                        </div>
                    </div>
                }
                {
                    showCalculateLink &&
                    <div className="col-3">
                        <div className="row">
                            <div className="col calc-dates">
                                <button className="btn btn-link" onClick={this.showCalculateDatesDrawer}>Calculate</button>
                            </div>
                        </div>
                    </div>
                }
            </Fragment>
        );
    }

    renderTitle = () => {
        return (
            <form noValidate className={this.state.isFormValid ? "" : "was-validated"}>
                <div className="row drawer-header">
                    {
                        this.renderStaticDateHeader()
                    }
                </div>
            </form>
        );
    }

    render() {
        let title = this.renderTitle();

        return (
            <div className="location-task-detail-drawer">
                <Drawer
                    isOpen={true}
                    isWide={true}
                    isMobile={this.props.isMobile}
                    title={title}
                    onSaveClick={this.onSave}
                    saveText='Save'
                    onCancelClick={this.props.onCancel}>

                    <LoadingIndicator isLoading={this.state.isSaving || this.props.isLoading} isFullscreen={true}>
                        <LocationTaskDetailDrawerForm
                            isFormValid={this.state.isFormValid}
                            currentUserId={this.props.userId}
                            task={this.props.task}
                            onTaskChange={this.onTaskChange}
                            responsibleUsers={this.props.responsibleUsers}
                            showContingentDrawer={this.props.showContingentDrawer}
                            showPrereqDrawer={this.props.showPrereqDrawer}
                            onDeleteContingentClick={this.onDeleteContingentClick}
                            statuses={this.props.filters.statuses}
                            departments={this.props.filters.departments}
                            communities={this.props.filters.communities}
                            isProjectWidgetActive={this.props.filters.isProjectWidgetActive}
                            isProjectIdRequiredForLocationTask={this.props.filters.isProjectIdRequiredForLocationTask}
                            projects={this.props.filters.projects}
                            phases={this.props.filters.phases}
                            contingentTasks={this.props.filters.contingentTasks}
                            successorTasks={this.props.filters.successorTasks}
                            isTaskStepsActive={this.props.filters.isTaskStepsActive}
                            isTaskDependenciesActive={this.props.filters.isTaskDependenciesActive}
                        />
                    </LoadingIndicator>
                </Drawer>
                {
                    this.state.showCalculateDatesDrawer &&
                    <CalculateDatesDrawer
                        task={this.props.task}
                        locationOpeningDate={this.props.filters.locationOpeningDate}
                        isStartDateActive={this.props.filters.isStartDateActive}
                        onTaskChange={this.onTaskChange}
                        onComplete={this.onCalculateDatesComplete}
                        onCancel={this.onCalculateDatesCancel}
                    />
                }
            </div>
        )
    }
}

LocationTaskDetailDrawer.propTypes = {
    task: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    filters: PropTypes.object.isRequired,
    responsibleUsers: PropTypes.array.isRequired,
    userId: PropTypes.number.isRequired,
    onTaskChange: PropTypes.func.isRequired,
    locationTaskActions: PropTypes.object.isRequired,
    filesActions: PropTypes.object.isRequired,
    showContingentDrawer: PropTypes.func,
    showPrereqDrawer: PropTypes.func,
    onComplete: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired
};

export default LocationTaskDetailDrawer;
